import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useAuth } from '../../contexts/AuthContext';
import {
  Trophy,
  Target,
  FileText,
  MessageCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  DollarSign,
  Building,
  Calendar,
  Star,
  ArrowRight,
  LayoutGrid,
  LayoutList,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Filter,
  GitBranch,
  User,
  Tag,
} from 'lucide-react';
import LeadToOpportunityModal from './LeadToOpportunityModal';
import LeadDetailModal from '../../entities/Leads/LeadDetailModal';
import OpportunityDetailModal from '../../entities/Opportunities/OpportunityDetailModal';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../utils/api';
import { loadPipelineStages, loadOpportunityStageMapping } from '../../utils/pipelineConfig';

const Pipeline = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'pipeline.title',
    'pipeline.subtitle',
    'pipeline.lead',
    'pipeline.opportunities',
    'pipeline.proposal',
    'pipeline.negotiate',
    'pipeline.won',
    'pipeline.lost',
    'common.refresh',
    'common.viewDetails'
  ]);
  const navigate = useNavigate();
  const { getProtectedTenantCode } = useTenantProtection();
  const { hasRole } = useAuth();

  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [opportunityStageMapping, setOpportunityStageMapping] = useState({});

  const [selectedLead, setSelectedLead] = useState(null);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [pendingTargetStageId, setPendingTargetStageId] = useState(null);
  
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [showOpportunityDetailModal, setShowOpportunityDetailModal] = useState(false);
  const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null);
  const [selectedOpportunityForDetail, setSelectedOpportunityForDetail] = useState(null);
  

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [layoutMode, setLayoutMode] = useState('horizontal'); // 'horizontal' or 'vertical'
  
  // Stage visibility state
  const [hiddenStages, setHiddenStages] = useState(new Set());
  
  // Date filter state
  const [dateField, setDateField] = useState('expectedCloseDate');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFullDateFilter, setShowFullDateFilter] = useState(false);
  
  // Salesperson filter state (for Admin/Manager)
  const [salespersonFilter, setSalespersonFilter] = useState('all');
  const [salespersons, setSalespersons] = useState([]);
  const [showSalespersonFilter, setShowSalespersonFilter] = useState(false);

  // Tag filter state (tags populated when user opens dropdown — same process as entity list pages)
  const [tagFilter, setTagFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  const [showTagFilter, setShowTagFilter] = useState(false);
  
  // Drag detection
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [isDragStarted, setIsDragStarted] = useState(false);
  const isDragStartedRef = useRef(false);
  const tagNormalizationCacheRef = useRef(new Map());

  // Icon mapping for dynamic icon loading
  const iconMap = {
    Target,
    Trophy,
    FileText,
    MessageCircle,
    CheckCircle,
    XCircle
  };

  // Load pipeline configuration
  useEffect(() => {
    const loadPipelineConfig = async () => {
      try {
        const tenantCode = getProtectedTenantCode();
        const [stagesData, mappingData] = await Promise.all([
          loadPipelineStages(tenantCode),
          loadOpportunityStageMapping(tenantCode)
        ]);
        
        setPipelineStages(stagesData);
        setOpportunityStageMapping(mappingData);
      } catch (error) {
        console.error('Error loading pipeline config:', error);
        // Fallback to default stages
        setPipelineStages([
          { id: 'lead', displayName: 'Lead', icon: 'Target', order: 1, enabled: true },
          { id: 'opportunities', displayName: 'Opportunities', icon: 'Trophy', order: 2, enabled: true },
          { id: 'proposal', displayName: 'Proposal', icon: 'FileText', order: 3, enabled: true },
          { id: 'negotiate', displayName: 'Negotiate', icon: 'MessageCircle', order: 4, enabled: true },
          { id: 'won', displayName: 'Won', icon: 'CheckCircle', order: 5, enabled: true },
          { id: 'lost', displayName: 'Lost', icon: 'XCircle', order: 6, enabled: true }
        ]);
        setOpportunityStageMapping({
          lead: ['Lead', 'Qualified'],
          opportunities: ['Lead', 'Qualified'],
          proposal: ['Proposal'],
          negotiate: ['Negotiation'],
          won: ['Closed Won'],
          lost: ['Closed Lost']
        });
      }
    };
    
    loadPipelineConfig();
  }, [getProtectedTenantCode]);

  // Helper functions for default styling (must be defined before useMemo that uses them)
  const getDefaultHeaderClass = (stageId) => {
    const defaults = {
      lead: 'from-indigo-500/90 to-indigo-400/80 text-white border-indigo-400/60 dark:from-indigo-700 dark:to-indigo-600 dark:text-slate-100 dark:border-indigo-700/60',
      opportunities: 'from-blue-500/90 to-blue-400/80 text-white border-blue-400/60 dark:from-blue-700 dark:to-blue-600 dark:text-slate-100 dark:border-blue-700/60',
      proposal: 'from-amber-500/90 to-amber-400/80 text-white border-amber-400/60 dark:from-amber-700 dark:to-amber-600 dark:text-slate-100 dark:border-amber-700/60',
      negotiate: 'from-orange-500/90 to-orange-400/80 text-white border-orange-400/60 dark:from-orange-700 dark:to-orange-600 dark:text-slate-100 dark:border-orange-700/60',
      won: 'from-emerald-500/90 to-emerald-400/80 text-white border-emerald-400/60 dark:from-emerald-700 dark:to-emerald-600 dark:text-slate-100 dark:border-emerald-700/60',
      lost: 'from-rose-500/90 to-rose-400/80 text-white border-rose-400/60 dark:from-rose-700 dark:to-rose-600 dark:text-slate-100 dark:border-rose-700/60'
    };
    return defaults[stageId] || defaults.lead;
  };

  const getDefaultLaneBg = (stageId) => {
    const defaults = {
      lead: 'bg-gradient-to-b from-indigo-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      opportunities: 'bg-gradient-to-b from-blue-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      proposal: 'bg-gradient-to-b from-amber-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      negotiate: 'bg-gradient-to-b from-orange-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      won: 'bg-gradient-to-b from-emerald-50/60 to-white dark:from-slate-800 dark:to-slate-900',
      lost: 'bg-gradient-to-b from-rose-50/60 to-white dark:from-slate-800 dark:to-slate-900'
    };
    return defaults[stageId] || defaults.lead;
  };

  // Transform pipeline stages from config to component format
  const stages = useMemo(() => {
    return pipelineStages
      .filter(stage => stage.enabled !== false)
      .map(stage => {
        const IconComponent = iconMap[stage.icon] || Target;
        // Use displayNameTH if language is Thai, otherwise use displayName
        let title = stage.displayName;
        if (i18n.language === 'th' && stage.displayNameTH) {
          title = stage.displayNameTH;
        } else {
          // Fallback to translation if available
          const titleKey = `pipeline.${stage.id}`;
          title = safeT(titleKey, stage.displayName);
        }
        
        return {
          id: stage.id,
          title,
          icon: IconComponent,
          headerClass: stage.headerClass || getDefaultHeaderClass(stage.id),
          laneBg: stage.laneBg || getDefaultLaneBg(stage.id)
        };
      });
  }, [pipelineStages, safeT, i18n.language]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [leadsData, oppsData] = await Promise.all([
        api.get('/leads?limit=100'),
        api.get('/opportunities?limit=100'),
      ]);

      setLeads(leadsData.leads || []);
      setOpportunities(oppsData.opportunities || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

     useEffect(() => {
     fetchData();
   }, []);

  // Fetch salespersons for filter (Admin/Manager only)
  useEffect(() => {
    const fetchSalespersons = async () => {
      if (!hasRole(['Admin', 'Manager'])) return;
      
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to only include users with Salesperson role
          const salespersonOnly = (data.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            return roles.includes('Salesperson');
          });
          setSalespersons(salespersonOnly);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
      }
    };
    
    fetchSalespersons();
  }, [hasRole]);

  // Close filter dropdowns when clicking outside
   useEffect(() => {
     const handleClickOutside = (event) => {
       if (showSalespersonFilter && !event.target.closest('.salesperson-filter-container')) {
         setShowSalespersonFilter(false);
       }
      if (showTagFilter && !event.target.closest('.tag-filter-container')) {
        setShowTagFilter(false);
      }
     };

     document.addEventListener('mousedown', handleClickOutside);
     return () => {
       document.removeEventListener('mousedown', handleClickOutside);
     };
  }, [showSalespersonFilter, showTagFilter]);

  // Populate available tags only when user opens the tag filter dropdown (same process as entity list tag filter)
  const collectTagsFromData = useCallback((leadsList, opportunitiesList) => {
    const tagSet = new Set();
    const collectTags = (tagsValue) => {
      if (!tagsValue) return;
      if (Array.isArray(tagsValue)) {
        tagsValue
          .map((t) => String(t).trim())
          .filter(Boolean)
          .forEach((t) => tagSet.add(t));
        return;
      }
      if (typeof tagsValue === 'string') {
        const trimmed = tagsValue.trim();
        if (!trimmed) return;
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              parsed
                .map((t) => String(t).trim())
                .filter(Boolean)
                .forEach((t) => tagSet.add(t));
              return;
            }
          } catch (err) {
            // fall through to CSV parsing
          }
        }
        trimmed
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => tagSet.add(t));
      }
    };
    leadsList.forEach((lead) => collectTags(lead.Tags));
    opportunitiesList.forEach((opp) => collectTags(opp.Tags));
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, []);

  const handleTagFilterToggle = useCallback(() => {
    if (!showTagFilter && availableTags.length === 0) {
      setAvailableTags(collectTagsFromData(leads, opportunities));
    }
    setShowTagFilter((prev) => !prev);
  }, [showTagFilter, availableTags.length, collectTagsFromData, leads, opportunities]);



  useEffect(() => {
    const reset = () => {
      setDraggedItem(null);
      setDragOverStage(null);
      setDragOverIndex(null);
      setIsDragging(false);
      setIsDragStarted(false);
    };
    const onKey = (e) => e.key === 'Escape' && reset();
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    window.addEventListener('mouseup', reset);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
      window.removeEventListener('mouseup', reset);
      window.removeEventListener('keyup', onKey);
    };
  }, []);

  const getStageItems = (stageId) => {
    let items = [];
    
    if (stageId === 'lead') {
      items = leads.filter((l) => {
        const isConverted = l.ConvertedToOpportunity;
        const isUnqualified = String(l.status || '').toLowerCase() === 'unqualified';
        // More robust check for unconverted leads
        // Show lead if ConvertedToOpportunity is 0, false, null, undefined, or not present
        return (!isConverted || isConverted === 0 || isConverted === null || isConverted === undefined) && !isUnqualified;
      });
    } else {
      // Use opportunity stage mapping from config
      const stageValues = opportunityStageMapping[stageId] || [];
      if (stageValues.length > 0) {
        items = opportunities.filter((o) => stageValues.includes(o.Stage));
      }
    }
    
    return getFilteredItems(items, stageId === 'lead' ? 'lead' : 'opportunity');
  };

  const getStageTotalValue = (stageId) => {
    const items = getStageItems(stageId);
    let total = 0;
    let currency = 'THB'; // default currency
    
    if (stageId === 'lead') {
      // Sum EstimatedBudget from leads
      items.forEach((lead) => {
        if (lead.EstimatedBudget) {
          total += Number(lead.EstimatedBudget) || 0;
          if (lead.Currency) currency = lead.Currency;
        }
      });
    } else {
      // Sum Value from opportunities
      items.forEach((opp) => {
        if (opp.Value) {
          total += Number(opp.Value) || 0;
          if (opp.Currency) currency = opp.Currency;
        }
      });
    }
    
    return { total, currency };
  };

  const handleConversionSubmit = async (leadId, opportunityData) => {
    try {
      const data = await api.post(`/leads/${leadId}/convert-to-opportunity`, opportunityData);
      const createdOpportunityId = data.opportunityId;

      setShowConversionModal(false);
      setSelectedLead(null);

      // If user dropped the lead onto a specific stage, move the new opportunity there
      if (createdOpportunityId && pendingTargetStageId && pendingTargetStageId !== 'opportunities') {
        // Use opportunity stage mapping from config
        const stageValues = opportunityStageMapping[pendingTargetStageId] || [];
        // Use the first stage value as the default
        const newStage = stageValues.length > 0 ? stageValues[0] : null;
        if (newStage) {
          await updateOpportunityStage(createdOpportunityId, newStage);
        }
      }

      setPendingTargetStageId(null);
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateOpportunityStage = async (opportunityId, newStage) => {
    const tenantCode = getProtectedTenantCode();
    const res = await fetch(`/api/opportunities/${opportunityId}/stage`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
      body: JSON.stringify({ stage: newStage }),
    });
    if (!res.ok) throw new Error('Failed to update stage');
    await fetchData();
  };

  const revertOpportunityToLead = async (opportunityId) => {
    const tenantCode = getProtectedTenantCode();
    const res = await fetch(`/api/opportunities/${opportunityId}/revert-to-lead`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
    });
    if (!res.ok) throw new Error('Failed to revert opportunity to lead');
    await fetchData();
  };

  const getOpportunityStageId = (opportunity) => {
    // Use opportunity stage mapping from config to find the stage ID
    for (const [stageId, stageValues] of Object.entries(opportunityStageMapping)) {
      if (stageValues.includes(opportunity.Stage)) {
        return stageId;
      }
    }
    // Default fallback
    return 'opportunities';
  };

  // Pointer-driven DnD (no HTML5 DnD) so cards are not tied to containers
  const findStageAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    let node = el;
    while (node) {
      if (node.dataset && node.dataset.stageId) return node.dataset.stageId;
      node = node.parentElement;
    }
    return null;
  };

  const handleCardDoubleClick = (item, type) => {
    if (type === 'lead') {
      setSelectedLeadForDetail(item);
      setShowLeadDetailModal(true);
    } else if (type === 'opportunity') {
      setSelectedOpportunityForDetail(item);
      setShowOpportunityDetailModal(true);
    }
  };

  const startCardDrag = (e, item, type) => {
    try {
      // Ignore non-left clicks or interactions on buttons/links/inputs
      if (e.button !== undefined && e.button !== 0) return;
      const interactive = e.target.closest('button, a, input, textarea, [role="button"], [contenteditable="true"]');
      if (interactive) return;
      e.preventDefault();
      e.stopPropagation();
      
      // Cache source elements to avoid null currentTarget later
      const sourceEl = e.currentTarget;
      // Try to resolve the nearest card root (more stable for rect)
      const cardRootEl = sourceEl && sourceEl.closest ? sourceEl.closest('[data-card-root="true"]') : null;

      // Store initial position for drag detection
      const startX = e.clientX;
      const startY = e.clientY;
      setDragStartPos({ x: startX, y: startY });

      const onMove = (ev) => {
        // Check if mouse has moved enough to consider it a drag
        const deltaX = Math.abs(ev.clientX - startX);
        const deltaY = Math.abs(ev.clientY - startY);
        const dragThreshold = 5; // 5px threshold
        
        if (!isDragStartedRef.current && (deltaX > dragThreshold || deltaY > dragThreshold)) {
          setIsDragStarted(true);
          isDragStartedRef.current = true;
          
          try {
            const rectEl = cardRootEl || sourceEl;
            const rect = rectEl && rectEl.getBoundingClientRect ? rectEl.getBoundingClientRect() : null;
            setDraggedItem({ ...item, type });
            setIsDragging(true);
            setDragOverStage(null);
            setDragOverIndex(null);
            setDragPos({ x: ev.clientX, y: ev.clientY });
            if (rect) {
              setDragOffset({ x: ev.clientX - rect.left, y: ev.clientY - rect.top });
            } else {
              // Fallback offset if rect cannot be resolved
              setDragOffset({ x: 16, y: 16 });
            }
          } catch (rectErr) {
            // Gracefully fallback if measuring fails
            setDraggedItem({ ...item, type });
            setIsDragging(true);
            setDragOverStage(null);
            setDragOverIndex(null);
            setDragPos({ x: ev.clientX, y: ev.clientY });
            setDragOffset({ x: 16, y: 16 });
          }
        }
        
        if (isDragStartedRef.current) {
          setDragPos({ x: ev.clientX, y: ev.clientY });
          const stageId = findStageAtPoint(ev.clientX, ev.clientY);
          setDragOverStage(stageId);
          setDragOverIndex(null);
        }
      };

      const onUp = async (ev) => {
        window.removeEventListener('mousemove', onMove);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        
        // Only process drop if we actually started dragging
        if (isDragStartedRef.current) {
          const targetStageId = findStageAtPoint(ev.clientX, ev.clientY);
          const current = { ...item, type };
          setIsDragging(false);
          setDraggedItem(null);
          setDragOverStage(null);
          setDragOverIndex(null);
          setIsDragStarted(false);
          isDragStartedRef.current = false;

          if (!targetStageId) return;
          try {
            if (type === 'lead') {
              if (targetStageId && targetStageId !== 'lead') {
                // Convert lead if it leaves the lead area, then (optionally) move to lane stage
                setSelectedLead(current);
                setPendingTargetStageId(targetStageId);
                setShowConversionModal(true);
              }
              return;
            }

            if (type === 'opportunity') {
              if (targetStageId && targetStageId === 'lead') {
                const currentStageTitle = stages.find(s => s.id === getOpportunityStageId(item))?.title || 'Current Stage';
                const targetStageTitle = stages.find(s => s.id === 'lead')?.title || 'Lead';
                const confirmMessage = `Are you sure you want to move this opportunity from "${currentStageTitle}" back to "${targetStageTitle}"?`;
                if (window.confirm(confirmMessage)) {
                  await revertOpportunityToLead(item.OpportunityID);
                }
                return;
              }
              
              // Use opportunity stage mapping from config
              const stageValues = opportunityStageMapping[targetStageId] || [];
              if (stageValues.length === 0) {
                return; // Invalid stage ID
              }
              
              // Use the first stage value as the default
              const newStage = stageValues[0];
              const targetStageTitle = stages.find(s => s.id === targetStageId)?.title || targetStageId;
              
              if (item.Stage !== newStage) {
                const currentStageId = getOpportunityStageId(item);
                const currentStageTitle = stages.find(s => s.id === currentStageId)?.title || 'Current Stage';
                const confirmMessage = `Are you sure you want to move this opportunity from "${currentStageTitle}" to "${targetStageTitle}"?`;
                if (window.confirm(confirmMessage)) {
                  await updateOpportunityStage(item.OpportunityID, newStage);
                }
              }
            }
          } catch (err) {
            setError(err.message || 'Drop failed');
          }
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true });
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    } catch (err) {
      setError('Drag start failed');
    }
  };

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

           const priorityColor = (priority) => {
      switch (priority) {
        case 'Critical':
          return 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/60 dark:text-white dark:border-rose-700/60';
        case 'High':
          return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/60 dark:text-white dark:border-orange-700/60';
        case 'Medium':
          return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/60 dark:text-white dark:border-amber-700/60';
        case 'Low':
          return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/60 dark:text-white dark:border-emerald-700/60';
        default:
          return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-white dark:border-slate-600';
      }
    };

  const getStageTotalColor = (stageId) => {
    switch (stageId) {
      case 'lead':
        return 'text-indigo-800 dark:text-indigo-200';
      case 'opportunities':
        return 'text-blue-800 dark:text-blue-200';
      case 'proposal':
        return 'text-amber-800 dark:text-amber-200';
      case 'negotiate':
        return 'text-orange-800 dark:text-orange-200';
      case 'won':
        return 'text-emerald-800 dark:text-emerald-200';
      case 'lost':
        return 'text-rose-800 dark:text-rose-200';
      default:
        return 'text-slate-800 dark:text-slate-200';
    }
  };

  const initials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  };

  const toggleStageVisibility = (stageId) => {
    setHiddenStages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stageId)) {
        newSet.delete(stageId);
      } else {
        newSet.add(stageId);
      }
      return newSet;
    });
  };

  // Date filter functions
  const getItemDateValue = (item, field, itemType) => {
    switch (field) {
      case 'createdAt':
        return item.CreatedAt;
      case 'lastActivityDate':
        return itemType === 'lead' ? item.LastContactDate : item.LastActivityDate;
      case 'nextFollowUpDate':
        return item.NextFollowUpDate;
      case 'expectedCloseDate':
        return item.ExpectedCloseDate;
      default:
        return null;
    }
  };

  const getRangeBoundaries = () => {
    const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
    const end = dateRange.end ? new Date(`${dateRange.end}T23:59:59.999`) : null;
    return { start, end };
  };

  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getQuickRanges = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Month ranges
    const thisMonthStart = new Date(currentYear, currentMonth, 1);
    const thisMonthEnd = new Date(currentYear, currentMonth + 1, 0);
    const nextMonthStart = new Date(currentYear, currentMonth + 1, 1);
    const nextMonthEnd = new Date(currentYear, currentMonth + 2, 0);
    
    // Quarter ranges
    const currentQuarter = Math.floor(currentMonth / 3);
    const thisQuarterStart = new Date(currentYear, currentQuarter * 3, 1);
    const thisQuarterEnd = new Date(currentYear, (currentQuarter + 1) * 3, 0);
    const nextQuarterStart = new Date(currentYear, (currentQuarter + 1) * 3, 1);
    const nextQuarterEnd = new Date(currentYear, (currentQuarter + 2) * 3, 0);
    
    // Year ranges
    const thisYearStart = new Date(currentYear, 0, 1);
    const thisYearEnd = new Date(currentYear, 11, 31);
    const nextYearStart = new Date(currentYear + 1, 0, 1);
    const nextYearEnd = new Date(currentYear + 1, 11, 31);
    
    // Specific quarters
    const q1Start = new Date(currentYear, 0, 1);
    const q1End = new Date(currentYear, 2, 31);
    const q2Start = new Date(currentYear, 3, 1);
    const q2End = new Date(currentYear, 5, 30);
    const q3Start = new Date(currentYear, 6, 1);
    const q3End = new Date(currentYear, 8, 30);
    const q4Start = new Date(currentYear, 9, 1);
    const q4End = new Date(currentYear, 11, 31);

    return [
      {
        key: 'thisMonth',
        label: safeT('pipeline.quickRange.thisMonth', 'This Month'),
        start: formatDateInput(thisMonthStart),
        end: formatDateInput(thisMonthEnd),
        category: 'relative'
      },
      {
        key: 'nextMonth',
        label: safeT('pipeline.quickRange.nextMonth', 'Next Month'),
        start: formatDateInput(nextMonthStart),
        end: formatDateInput(nextMonthEnd),
        category: 'relative'
      },
      {
        key: 'thisQuarter',
        label: safeT('pipeline.quickRange.thisQuarter', 'This Quarter'),
        start: formatDateInput(thisQuarterStart),
        end: formatDateInput(thisQuarterEnd),
        category: 'relative'
      },
      {
        key: 'nextQuarter',
        label: safeT('pipeline.quickRange.nextQuarter', 'Next Quarter'),
        start: formatDateInput(nextQuarterStart),
        end: formatDateInput(nextQuarterEnd),
        category: 'relative'
      },
      {
        key: 'thisYear',
        label: safeT('pipeline.quickRange.thisYear', 'This Year'),
        start: formatDateInput(thisYearStart),
        end: formatDateInput(thisYearEnd),
        category: 'relative'
      },
      {
        key: 'nextYear',
        label: safeT('pipeline.quickRange.nextYear', 'Next Year'),
        start: formatDateInput(nextYearStart),
        end: formatDateInput(nextYearEnd),
        category: 'relative'
      },
      {
        key: 'q1',
        label: 'Q1',
        start: formatDateInput(q1Start),
        end: formatDateInput(q1End),
        category: 'quarter'
      },
      {
        key: 'q2',
        label: 'Q2',
        start: formatDateInput(q2Start),
        end: formatDateInput(q2End),
        category: 'quarter'
      },
      {
        key: 'q3',
        label: 'Q3',
        start: formatDateInput(q3Start),
        end: formatDateInput(q3End),
        category: 'quarter'
      },
      {
        key: 'q4',
        label: 'Q4',
        start: formatDateInput(q4Start),
        end: formatDateInput(q4End),
        category: 'quarter'
      }
    ];
  };
  
  const getMonthOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const months = [];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      const monthName = monthStart.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
      
      months.push({
        key: `month-${i}`,
        label: monthName,
        start: formatDateInput(monthStart),
        end: formatDateInput(monthEnd)
      });
    }
    
    return months;
  };

  const isInDateRange = (item, itemType) => {
    const { start, end } = getRangeBoundaries();
    if (!start && !end) return true;

    const itemDate = getItemDateValue(item, dateField, itemType);
    if (!itemDate) return false;

    const itemDateTime = new Date(itemDate);
    if (start && itemDateTime < start) return false;
    if (end && itemDateTime > end) return false;
    return true;
  };

  const normalizeTagList = (tagsValue) => {
    if (!tagsValue) return [];
    if (Array.isArray(tagsValue)) {
      return tagsValue.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
    }
    if (typeof tagsValue === 'string') {
      const trimmed = tagsValue.trim();
      if (!trimmed) return [];
      const cacheKey = `str:${trimmed}`;
      const cached = tagNormalizationCacheRef.current.get(cacheKey);
      if (cached) {
        return cached;
      }

      let normalized = [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            normalized = parsed.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
            if (tagNormalizationCacheRef.current.size > 500) {
              tagNormalizationCacheRef.current.clear();
            }
            tagNormalizationCacheRef.current.set(cacheKey, normalized);
            return normalized;
          }
        } catch (err) {
          // fall through to CSV parsing
        }
      }
      normalized = trimmed
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (tagNormalizationCacheRef.current.size > 500) {
        tagNormalizationCacheRef.current.clear();
      }
      tagNormalizationCacheRef.current.set(cacheKey, normalized);
      return normalized;
    }
    return [];
  };

  const getFilteredItems = (items, itemType) => {
    return items.filter(item => {
      // Date filter
      if (!isInDateRange(item, itemType)) return false;
      
      // Salesperson filter (only applied if not 'all')
      if (salespersonFilter !== 'all') {
        const itemSalespersonId = item.SalespersonID || item.OwnerID || item.AssignedToUserID;
        if (String(itemSalespersonId) !== String(salespersonFilter)) return false;
      }

      // Tag filter (only applied if not 'all')
      if (tagFilter !== 'all') {
        const itemTags = normalizeTagList(item.Tags);
        if (!itemTags.includes(String(tagFilter).toLowerCase())) return false;
      }
      
      return true;
    });
  };

  // Helper to check salesperson filter
  const matchesSalespersonFilter = (item) => {
    if (salespersonFilter === 'all') return true;
    const itemSalespersonId = item.SalespersonID || item.OwnerID || item.AssignedToUserID;
    return String(itemSalespersonId) === String(salespersonFilter);
  };

  const matchesTagFilter = (item) => {
    if (tagFilter === 'all') return true;
    const itemTags = normalizeTagList(item.Tags);
    return itemTags.includes(String(tagFilter).toLowerCase());
  };

  const filteredLeadsForStats = useMemo(() => {
    return leads.filter((lead) => {
      const isUnconverted = !lead.ConvertedToOpportunity || lead.ConvertedToOpportunity === 0 || lead.ConvertedToOpportunity === null || lead.ConvertedToOpportunity === undefined;
      const isUnqualified = String(lead.status || '').toLowerCase() === 'unqualified';
      return isUnconverted && !isUnqualified && isInDateRange(lead, 'lead') && matchesSalespersonFilter(lead) && matchesTagFilter(lead);
    });
  }, [leads, dateField, dateRange, salespersonFilter, tagFilter]);

  const filteredOpportunitiesForStats = useMemo(() => {
    return opportunities.filter((opp) => isInDateRange(opp, 'opportunity') && matchesSalespersonFilter(opp) && matchesTagFilter(opp));
  }, [opportunities, dateField, dateRange, salespersonFilter, tagFilter]);

  // Calculate stats for header badges (must be before early returns)
  const totalLeads = useMemo(() => filteredLeadsForStats.length, [filteredLeadsForStats]);

  const totalOpportunities = useMemo(() => filteredOpportunitiesForStats.length, [filteredOpportunitiesForStats]);

  const totalLeadValue = useMemo(() => {
    let total = 0;
    let currency = 'THB';
    filteredLeadsForStats.forEach((lead) => {
      if (lead.EstimatedBudget) {
        total += Number(lead.EstimatedBudget) || 0;
        if (lead.Currency) currency = lead.Currency;
      }
    });
    return { total, currency };
  }, [filteredLeadsForStats]);

  const totalOpportunityValue = useMemo(() => {
    let total = 0;
    let currency = 'THB';
    filteredOpportunitiesForStats.forEach((opp) => {
      if (opp.Value) {
        total += Number(opp.Value) || 0;
        if (opp.Currency) currency = opp.Currency;
      }
    });
    return { total, currency };
  }, [filteredOpportunitiesForStats]);

  const LeadCard = ({ lead, stageId, index }) => {
    const [showDateTooltip, setShowDateTooltip] = useState(false);
    
    return (
      <div
        data-card-root="true"
        className={`group relative rounded-xl border shadow-md bg-yellow-50/90 backdrop-blur p-4 w-full transition-all hover:shadow-xl hover:-translate-y-0.5 cursor-grab ${
          draggedItem && draggedItem.LeadID === lead.LeadID ? 'opacity-60 scale-[1.02]' : ''
        } ${dragOverStage === stageId && dragOverIndex === index ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-slate-200'}`}
        onMouseDown={(e) => startCardDrag(e, lead, 'lead')}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCardDoubleClick(lead, 'lead');
        }}
      >
        <div className="flex items-start gap-3">

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                                        <h4
                className="text-sm font-semibold text-slate-900 truncate hover:text-indigo-700 cursor-pointer"
                  title=""
              >
                {lead.CompanyName || lead.ContactName || `Lead #${lead.LeadID}`}
              </h4>
              </div>
            </div>
            {lead.Title && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 truncate">
                <FileText className="h-3.5 w-3.5" />
                <span className="font-medium text-slate-800 truncate">{lead.Title}</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 space-y-2 text-xs text-slate-600">
          {/* Value and Score on same line */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {lead.EstimatedBudget && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <DollarSign className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="truncate font-medium">{formatCurrencyLocal(lead.EstimatedBudget, lead.Currency)}</span>
                </div>
              )}
              {lead.Score && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  <span className="font-medium">{lead.Score}/100</span>
                </div>
              )}
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${priorityColor(lead.Priority)}`}>
              {lead.Priority || 'Normal'}
            </span>
          </div>
          {/* Date with label and tooltip */}
          {(lead.CreatedAt || lead.LastContactDate || lead.UpdatedAt) && (
            <div className="relative">
              <div 
                className="flex items-center gap-1.5 cursor-help"
                onMouseEnter={() => setShowDateTooltip(true)}
                onMouseLeave={() => setShowDateTooltip(false)}
              >
                <Calendar className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-slate-500 font-medium">Created:</span>
                <span className="truncate">{formatDate(lead.CreatedAt || lead.LastContactDate || lead.UpdatedAt)}</span>
              </div>
              {showDateTooltip && (
                <div className="absolute bottom-full left-0 mb-2 z-50 bg-slate-800 text-white text-xs rounded-lg shadow-lg p-3 min-w-64 pointer-events-none">
                  <div className="space-y-1.5">
                    <div className="font-semibold text-slate-300 mb-2">All Dates</div>
                    {lead.CreatedAt && (
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-400">Created:</span>
                        <span className="font-medium">{formatDate(lead.CreatedAt)}</span>
                      </div>
                    )}
                    {lead.LastContactDate && (
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-400">Last Contact:</span>
                        <span className="font-medium">{formatDate(lead.LastContactDate)}</span>
                      </div>
                    )}
                    {lead.NextFollowUpDate && (
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-400">Next Follow-up:</span>
                        <span className="font-medium">{formatDate(lead.NextFollowUpDate)}</span>
                      </div>
                    )}
                    {lead.UpdatedAt && (
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-400">Updated:</span>
                        <span className="font-medium">{formatDate(lead.UpdatedAt)}</span>
                      </div>
                    )}
                  </div>
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                </div>
              )}
            </div>
          )}
        </div>
          <div className="mt-4 flex justify-end">
            <button
              aria-label={safeT('common.viewDetails', 'View Details')}
              title={safeT('common.viewDetails', 'View Details')}
              className="inline-flex items-center justify-center rounded-lg bg-slate-200 text-slate-800 p-2 hover:bg-slate-300 active:bg-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedLeadForDetail(lead);
                setShowLeadDetailModal(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
         </div>
      </div>
    );
  };

     const OpportunityCard = ({ opportunity, stageId, index }) => {
     const probability = Number(opportunity.Probability || 0);
     const isClosedStage = stageId === 'won' || stageId === 'lost';
     const [showDateTooltip, setShowDateTooltip] = useState(false);
     
     return (
       <div
         data-card-root="true"
         className={`group relative rounded-xl border shadow-md bg-yellow-50/90 backdrop-blur p-4 w-full transition-all hover:shadow-xl hover:-translate-y-0.5 cursor-grab ${
           draggedItem && draggedItem.OpportunityID === opportunity.OpportunityID ? 'opacity-60 scale-[1.02]' : ''
         } ${dragOverStage === stageId && dragOverIndex === index ? 'border-blue-400 ring-2 ring-blue-200' : 'border-slate-200'}`}
         onMouseDown={(e) => startCardDrag(e, opportunity, 'opportunity')}
         onDoubleClick={(e) => {
           e.preventDefault();
           e.stopPropagation();
           handleCardDoubleClick(opportunity, 'opportunity');
         }}
       >
         {isClosedStage ? (
           // Compact layout for Won/Lost stages
           <div className="space-y-3">
             <div>
               <h4 className="text-sm font-semibold text-slate-900 truncate hover:text-blue-700 cursor-pointer">
                 {opportunity.CustomerName || opportunity.OpportunityName || `Opportunity #${opportunity.OpportunityID}`}
               </h4>
               {opportunity.OpportunityName && opportunity.CustomerName && (
                 <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 truncate">
                   <FileText className="h-3.5 w-3.5" />
                   <span className="font-medium text-slate-800 truncate">{opportunity.OpportunityName}</span>
                 </div>
               )}
             </div>
             
             <div className="space-y-2 text-xs text-slate-600">
               {/* Value and Probability on same line */}
               <div className="flex items-center gap-3">
                 {opportunity.Value && (
                   <div className="flex items-center gap-1.5 min-w-0">
                     <DollarSign className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                     <span className="truncate font-medium">{formatCurrencyLocal(opportunity.Value, opportunity.Currency)}</span>
                   </div>
                 )}
                 {probability > 0 && (
                   <div className="flex items-center gap-1.5 flex-shrink-0">
                     <Star className="h-3.5 w-3.5 text-amber-500" />
                     <span className="font-medium">{probability}%</span>
                   </div>
                 )}
               </div>
               {/* Date with label and tooltip */}
               {(opportunity.ExpectedCloseDate || opportunity.ClosedDate) && (
                 <div className="relative">
                   <div 
                     className="flex items-center gap-1.5 cursor-help"
                     onMouseEnter={() => setShowDateTooltip(true)}
                     onMouseLeave={() => setShowDateTooltip(false)}
                   >
                     <Calendar className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                     <span className="text-slate-500 font-medium">
                       {opportunity.ClosedDate ? 'Closed:' : 'Expected Close:'}
                     </span>
                     <span className="truncate">{formatDate(opportunity.ExpectedCloseDate || opportunity.ClosedDate)}</span>
                   </div>
                   {showDateTooltip && (
                     <div className="absolute bottom-full left-0 mb-2 z-50 bg-slate-800 text-white text-xs rounded-lg shadow-lg p-3 min-w-64 pointer-events-none">
                       <div className="space-y-1.5">
                         <div className="font-semibold text-slate-300 mb-2">All Dates</div>
                         {opportunity.CreatedAt && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Created:</span>
                             <span className="font-medium">{formatDate(opportunity.CreatedAt)}</span>
                           </div>
                         )}
                         {opportunity.LastActivityDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Last Activity:</span>
                             <span className="font-medium">{formatDate(opportunity.LastActivityDate)}</span>
                           </div>
                         )}
                         {opportunity.NextFollowUpDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Next Follow-up:</span>
                             <span className="font-medium">{formatDate(opportunity.NextFollowUpDate)}</span>
                           </div>
                         )}
                         {opportunity.ExpectedCloseDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Expected Close:</span>
                             <span className="font-medium">{formatDate(opportunity.ExpectedCloseDate)}</span>
                           </div>
                         )}
                         {opportunity.ClosedDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Closed:</span>
                             <span className="font-medium">{formatDate(opportunity.ClosedDate)}</span>
                           </div>
                         )}
                         {opportunity.UpdatedAt && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Updated:</span>
                             <span className="font-medium">{formatDate(opportunity.UpdatedAt)}</span>
                           </div>
                         )}
                       </div>
                       <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                     </div>
                   )}
                 </div>
               )}
             </div>
             <div className="mt-3 flex justify-end">
               <button
                 aria-label={safeT('common.viewDetails', 'View Details')}
                 title={safeT('common.viewDetails', 'View Details')}
                 className="inline-flex items-center justify-center rounded-lg bg-slate-200 text-slate-800 p-2 hover:bg-slate-300 active:bg-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setSelectedOpportunityForDetail(opportunity);
                   setShowOpportunityDetailModal(true);
                 }}
               >
                 <Eye className="h-4 w-4" />
               </button>
             </div>
           </div>
         ) : (
           // Full layout for other stages
           <>
             <div className="flex items-start gap-3">
               <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <h4
                      className="text-sm font-semibold text-slate-900 truncate hover:text-blue-700 cursor-pointer"
                      title=""
                    >
                      {opportunity.CustomerName || opportunity.OpportunityName || `Opportunity #${opportunity.OpportunityID}`}
                    </h4>
                  </div>
                </div>
                {opportunity.OpportunityName && opportunity.CustomerName && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 truncate">
                    <FileText className="h-3.5 w-3.5" />
                    <span className="font-medium text-slate-800 truncate">{opportunity.OpportunityName}</span>
                  </div>
                )}
               </div>
             </div>
             
             <div className="mt-3 space-y-2 text-xs text-slate-600">
               {/* Value and Probability on same line */}
               <div className="flex items-center justify-between gap-2">
                 <div className="flex items-center gap-3 flex-1 min-w-0">
                   {opportunity.Value && (
                     <div className="flex items-center gap-1.5 min-w-0">
                       <DollarSign className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                       <span className="truncate font-medium">{formatCurrencyLocal(opportunity.Value, opportunity.Currency)}</span>
                     </div>
                   )}
                   {probability > 0 && (
                     <div className="flex items-center gap-1.5 flex-shrink-0">
                       <Star className="h-3.5 w-3.5 text-amber-500" />
                       <span className="font-medium">{probability}%</span>
                     </div>
                   )}
                 </div>
                 <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${priorityColor(opportunity.Priority)}`}>
                   {opportunity.Priority || 'Normal'}
                 </span>
               </div>
               {/* Date with label and tooltip */}
               {opportunity.ExpectedCloseDate && (
                 <div className="relative">
                   <div 
                     className="flex items-center gap-1.5 cursor-help"
                     onMouseEnter={() => setShowDateTooltip(true)}
                     onMouseLeave={() => setShowDateTooltip(false)}
                   >
                     <Calendar className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                     <span className="text-slate-500 font-medium">Expected Close:</span>
                     <span className="truncate">{formatDate(opportunity.ExpectedCloseDate)}</span>
                   </div>
                   {showDateTooltip && (
                     <div className="absolute bottom-full left-0 mb-2 z-50 bg-slate-800 text-white text-xs rounded-lg shadow-lg p-3 min-w-64 pointer-events-none">
                       <div className="space-y-1.5">
                         <div className="font-semibold text-slate-300 mb-2">All Dates</div>
                         {opportunity.CreatedAt && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Created:</span>
                             <span className="font-medium">{formatDate(opportunity.CreatedAt)}</span>
                           </div>
                         )}
                         {opportunity.LastActivityDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Last Activity:</span>
                             <span className="font-medium">{formatDate(opportunity.LastActivityDate)}</span>
                           </div>
                         )}
                         {opportunity.NextFollowUpDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Next Follow-up:</span>
                             <span className="font-medium">{formatDate(opportunity.NextFollowUpDate)}</span>
                           </div>
                         )}
                         {opportunity.ExpectedCloseDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Expected Close:</span>
                             <span className="font-medium">{formatDate(opportunity.ExpectedCloseDate)}</span>
                           </div>
                         )}
                         {opportunity.ClosedDate && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Closed:</span>
                             <span className="font-medium">{formatDate(opportunity.ClosedDate)}</span>
                           </div>
                         )}
                         {opportunity.UpdatedAt && (
                           <div className="flex justify-between gap-4">
                             <span className="text-slate-400">Updated:</span>
                             <span className="font-medium">{formatDate(opportunity.UpdatedAt)}</span>
                           </div>
                         )}
                       </div>
                       <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                     </div>
                   )}
                 </div>
               )}
             </div>
             
             <div className="mt-4 flex justify-end">
               <button
                 aria-label={safeT('common.viewDetails', 'View Details')}
                 title={safeT('common.viewDetails', 'View Details')}
                 className="inline-flex items-center justify-center rounded-lg bg-slate-200 text-slate-800 p-2 hover:bg-slate-300 active:bg-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setSelectedOpportunityForDetail(opportunity);
                   setShowOpportunityDetailModal(true);
                 }}
               >
                 <Eye className="h-4 w-4" />
               </button>
             </div>
           </>
         )}
       </div>
     );
   };

  const showTranslationLoading = !translationsReady || !ready;

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-2">
        {showTranslationLoading && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Loading translations...
          </div>
        )}
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {showTranslationLoading && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Loading translations...
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex-shrink-0">
                <Target className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">
                  {safeT('pipeline.title', 'Sales Pipeline')}
                </h1>
                <p className="text-teal-100 mt-1 text-sm sm:text-base truncate">
                  {safeT('pipeline.subtitle', 'Manage your sales process through different stages')}
                  {isDragging && (
                    <span className="ml-2 flex items-center text-teal-200">
                      <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                      <span className="text-xs">Dragging...</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <Target className="h-4 w-4" />
                <span className="text-sm">
                  {totalLeads} {safeT('pipeline.lead', 'Leads')}
                </span>
                {totalLeadValue.total > 0 && (
                  <span className="text-sm font-semibold ml-2">
                    ({formatCurrencyLocal(totalLeadValue.total, totalLeadValue.currency)})
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <Trophy className="h-4 w-4" />
                <span className="text-sm">
                  {totalOpportunities} {safeT('pipeline.opportunities', 'Opportunities')}
                </span>
                {totalOpportunityValue.total > 0 && (
                  <span className="text-sm font-semibold ml-2">
                    ({formatCurrencyLocal(totalOpportunityValue.total, totalOpportunityValue.currency)})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Bar - Below Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                <button
                  onClick={() => setLayoutMode('horizontal')}
                  className={`p-2 rounded-md transition-colors ${
                    layoutMode === 'horizontal'
                      ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  }`}
                  title="Horizontal Layout"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setLayoutMode('vertical')}
                  className={`p-2 rounded-md transition-colors ${
                    layoutMode === 'vertical'
                      ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  }`}
                  title="Vertical Layout"
                >
                  <LayoutList className="h-4 w-4" />
                </button>
              </div>
              
              {/* Date Filter */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300">
                  <Filter className="h-4 w-4" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {safeT('pipeline.dateFilterLabel', 'Date')}
                  </span>
                  <select
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                    className="bg-transparent text-sm font-medium outline-none"
                    aria-label={safeT('pipeline.dateFilterLabel', 'Date')}
                  >
                    <option value="createdAt">{safeT('pipeline.dateField.createDate', 'Create Date')}</option>
                    <option value="lastActivityDate">{safeT('pipeline.dateField.lastActivityDate', 'Last Activity Date')}</option>
                    <option value="nextFollowUpDate">{safeT('pipeline.dateField.nextFollowUpDate', 'Next Follow-up Date')}</option>
                    <option value="expectedCloseDate">{safeT('pipeline.dateField.expectedCloseDate', 'Expected Close Date')}</option>
                  </select>
                </div>
                
                {/* Quick Filters - Always Visible */}
                {getQuickRanges().filter(r => r.category === 'relative' && !['thisQuarter', 'nextQuarter'].includes(r.key)).map((range) => {
                  const isActive = dateRange.start === range.start && dateRange.end === range.end;
                  return (
                    <button
                      key={range.key}
                      onClick={() => setDateRange({ start: range.start, end: range.end })}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        isActive
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/20 dark:border-indigo-700 dark:text-indigo-300'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-sm">{range.label}</span>
                    </button>
                  );
                })}
                
                {/* Toggle Full Filter Button */}
                <button
                  onClick={() => setShowFullDateFilter(!showFullDateFilter)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                    showFullDateFilter
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/20 dark:border-indigo-700 dark:text-indigo-300'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                  }`}
                  title={safeT('pipeline.showMoreFilters', 'Show more filters')}
                >
                  <Filter className="h-4 w-4" />
                  {showFullDateFilter ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                
                {(dateRange.start || dateRange.end) && (
                  <button
                    onClick={() => setDateRange({ start: '', end: '' })}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/40"
                  >
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">{safeT('pipeline.dateRange.clear', 'Clear')}</span>
                  </button>
                )}
              </div>
              
              {/* Expanded Date Filter Options */}
              {showFullDateFilter && (
                <div className="flex flex-wrap items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  {/* Custom Date Range */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Custom Range:</span>
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {safeT('pipeline.dateRange.from', 'From')}
                      </span>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        className="bg-transparent text-sm outline-none"
                        aria-label={safeT('pipeline.dateRange.from', 'From')}
                      />
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {safeT('pipeline.dateRange.to', 'To')}
                      </span>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        className="bg-transparent text-sm outline-none"
                        aria-label={safeT('pipeline.dateRange.to', 'To')}
                      />
                    </div>
                  </div>
                  
                  {/* Quarter Filters */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Quarters:</span>
                    {getQuickRanges().filter(r => r.category === 'quarter').map((range) => {
                      const isActive = dateRange.start === range.start && dateRange.end === range.end;
                      return (
                        <button
                          key={range.key}
                          onClick={() => setDateRange({ start: range.start, end: range.end })}
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-lg border transition-colors min-w-[3rem] ${
                            isActive
                              ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-semibold dark:bg-indigo-900/20 dark:border-indigo-700 dark:text-indigo-300'
                              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                          }`}
                        >
                          <span className="text-sm font-medium">{range.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Month Dropdown */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Select Month:</span>
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300">
                      <Calendar className="h-4 w-4" />
                      <select
                        value=""
                        onChange={(e) => {
                          const selected = getMonthOptions().find(m => m.key === e.target.value);
                          if (selected) {
                            setDateRange({ start: selected.start, end: selected.end });
                          }
                        }}
                        className="bg-transparent text-sm outline-none min-w-[180px]"
                        aria-label="Select Month"
                      >
                        <option value="">{safeT('pipeline.selectMonth', 'Select a month...')}</option>
                        {getMonthOptions().map((month) => (
                          <option key={month.key} value={month.key}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Salesperson Filter (Admin/Manager only) */}
              {hasRole(['Admin', 'Manager']) && salespersons.length > 0 && (
                <div className="relative salesperson-filter-container">
                  <button
                    onClick={() => setShowSalespersonFilter(!showSalespersonFilter)}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      salespersonFilter !== 'all'
                        ? 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                    title={safeT('pipeline.filterBySalesperson', 'Filter by salesperson')}
                  >
                    <User className="h-4 w-4" />
                    <span className="text-sm font-medium truncate max-w-32">
                      {salespersonFilter === 'all' 
                        ? safeT('pipeline.allSalespersons', 'All Salespersons')
                        : (() => {
                            const selected = salespersons.find(s => String(s.UserID) === String(salespersonFilter));
                            return selected ? `${selected.FirstName} ${selected.LastName}` : safeT('pipeline.allSalespersons', 'All Salespersons');
                          })()
                      }
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {showSalespersonFilter && (
                    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 min-w-56 max-h-72 overflow-y-auto">
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            setSalespersonFilter('all');
                            setShowSalespersonFilter(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            salespersonFilter === 'all'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                              : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                          }`}
                        >
                          {safeT('pipeline.allSalespersons', 'All Salespersons')}
                        </button>
                        {salespersons.map(salesperson => (
                          <button
                            key={salesperson.UserID}
                            onClick={() => {
                              setSalespersonFilter(String(salesperson.UserID));
                              setShowSalespersonFilter(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                              String(salespersonFilter) === String(salesperson.UserID)
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                          >
                            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-medium">
                              {salesperson.FirstName?.charAt(0)}{salesperson.LastName?.charAt(0)}
                            </div>
                            <span className="truncate">{salesperson.FirstName} {salesperson.LastName}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tag Filter — tags populated when user opens dropdown (same process as entity list pages) */}
              <div className="relative tag-filter-container">
                <button
                  onClick={handleTagFilterToggle}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      tagFilter !== 'all'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                    title={safeT('pipeline.filterByTag', 'Filter by tag')}
                  >
                    <Tag className="h-4 w-4" />
                    <span className="text-sm font-medium truncate max-w-32">
                      {tagFilter === 'all' ? safeT('pipeline.allTags', 'All Tags') : tagFilter}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showTagFilter && (
                    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 min-w-56 max-h-72 overflow-y-auto">
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            setTagFilter('all');
                            setShowTagFilter(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            tagFilter === 'all'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                              : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                          }`}
                        >
                          {safeT('pipeline.allTags', 'All Tags')}
                        </button>
                        {availableTags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => {
                              setTagFilter(tag);
                              setShowTagFilter(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              String(tagFilter).toLowerCase() === String(tag).toLowerCase()
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            
            <div className="flex items-center gap-2">
              {hasRole(['Admin']) && (
                <button
                  onClick={() => navigate('/pipeline-studio')}
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg shadow-sm dark:bg-purple-500 dark:hover:bg-purple-600"
                  title={safeT('pipeline.studio.title', 'Pipeline Studio')}
                >
                  <GitBranch className="h-4 w-4" />
                  <span className="text-xs sm:text-sm hidden sm:inline">{safeT('pipeline.studio.title', 'Studio')}</span>
                </button>
              )}
              <button
                onClick={fetchData}
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg shadow-sm dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <RefreshCw className="h-4 w-4" /> <span className="text-xs sm:text-sm">{safeT('common.refresh', 'Refresh')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm dark:bg-rose-900/40 dark:border-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

                          <div className="relative">
                   <div className={`${
           layoutMode === 'horizontal' 
             ? 'flex gap-4 pb-4' 
             : 'grid grid-cols-1 gap-6 pb-4'
         }`}>
            {stages.map((stage) => {
              const Icon = stage.icon;
              const items = getStageItems(stage.id);
              const headerId = `lane-header-${stage.id}`;
              const stageTotal = getStageTotalValue(stage.id);
              return (
                                 <section
                   key={stage.id}
                   aria-labelledby={headerId}
                   data-stage-id={stage.id}
                   className={`rounded-2xl border ${stage.laneBg} border-slate-200 dark:border-slate-700 shadow-sm transition-shadow ${
                     isDragging && dragOverStage === stage.id ? 'ring-2 ring-indigo-300 dark:ring-indigo-600' : ''
                   } ${
                     layoutMode === 'horizontal' 
                       ? hiddenStages.has(stage.id) 
                         ? 'w-16 flex-shrink-0 min-w-[4rem]' 
                         : 'flex-1 min-w-0'
                       : ''
                   }`}
                 >
                                                  <header
                    id={headerId}
                    className={`sticky top-0 z-10 rounded-t-2xl border-b bg-gradient-to-r ${stage.headerClass} ${
                      layoutMode === 'horizontal' && hiddenStages.has(stage.id)
                        ? 'px-1.5 py-2 min-w-0'
                        : 'px-3 py-2 sm:px-4 sm:py-3'
                    }`}
                  >
                                                                              <div className={`${
                       layoutMode === 'horizontal' && hiddenStages.has(stage.id)
                         ? 'flex flex-col justify-center items-center gap-1.5 h-full'
                         : 'flex flex-col gap-1'
                     }`}>
                       {layoutMode === 'horizontal' && hiddenStages.has(stage.id) ? (
                         <>
                           <div className="flex flex-col items-center gap-1 flex-shrink-0">
                             <Icon className="h-4 w-4" />
                             <h3 className="text-[10px] font-semibold tracking-wide writing-mode-vertical-rl text-center whitespace-nowrap">
                               {stage.title}
                             </h3>
                           </div>
                           <button
                             onClick={(e) => {
                               e.preventDefault();
                               e.stopPropagation();
                               toggleStageVisibility(stage.id);
                             }}
                             className="mt-auto p-1.5 rounded-md hover:bg-white/30 active:bg-white/40 transition-colors flex-shrink-0 z-20 relative"
                             title="Show stage"
                             aria-label="Show stage"
                           >
                             <Eye className="h-4 w-4" />
                           </button>
                         </>
                       ) : (
                         <>
                           {/* First row: Title, Count badge, and Hide button */}
                           <div className="flex items-center justify-between gap-2">
                             <div className="flex items-center gap-2 min-w-0">
                               <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5 flex-shrink-0" />
                               <h3 className="text-xs sm:text-sm font-semibold tracking-wide truncate">{stage.title}</h3>
                               <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/20 flex-shrink-0">
                                 {items.length}
                               </span>
                             </div>
                             <button
                               onClick={() => toggleStageVisibility(stage.id)}
                               className="p-1 rounded-md hover:bg-white/20 transition-colors flex-shrink-0"
                               title={hiddenStages.has(stage.id) ? 'Show stage' : 'Hide stage'}
                             >
                               {hiddenStages.has(stage.id) ? (
                                 <Eye className="h-4 w-4" />
                               ) : (
                                 <EyeOff className="h-4 w-4" />
                               )}
                             </button>
                           </div>
                           {/* Second row: Total value */}
                           {stageTotal.total > 0 && (
                             <div className="flex items-center">
                               <span className={`text-sm sm:text-base md:text-lg lg:text-xl font-bold ${getStageTotalColor(stage.id)} px-2 py-0.5 rounded-full bg-white/20 truncate`}>
                                 {formatCurrencyLocal(stageTotal.total, stageTotal.currency)}
                               </span>
                             </div>
                           )}
                         </>
                       )}
                    </div>
                 </header>

                 {!hiddenStages.has(stage.id) && (
                   <div className={`p-3 space-y-3 ${
                     layoutMode === 'vertical' ? 'max-h-96 overflow-y-auto' : ''
                   }`}>
                   {items.length === 0 && (
                     <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-white/60 dark:bg-slate-800/60 p-4 text-center text-xs text-slate-500 dark:text-slate-400">
                       {draggedItem
                         ? draggedItem.type === 'lead' && stage.id === 'opportunities'
                           ? 'Drop here to convert to opportunity'
                           : draggedItem.type === 'opportunity' && stage.id !== 'lead'
                             ? `Drop here to move to ${stage.title}`
                             : 'Drop here'
                         : 'No items in this stage'}
                     </div>
                   )}

                                       <div className={`${
                      layoutMode === 'vertical' 
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3' 
                        : 'space-y-3'
                    }`}>
                      {items.map((item, index) => (
                        <div key={`${stage.id}-${stage.id === 'lead' ? item.LeadID : item.OpportunityID}`}>
                          {stage.id === 'lead' ? (
                            <LeadCard lead={item} stageId={stage.id} index={index} />
                          ) : (
                            <OpportunityCard opportunity={item} stageId={stage.id} index={index} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

                 {isDragging && draggedItem && (
           <div
             className="fixed z-50 pointer-events-none w-80"
             style={{ left: `${dragPos.x - dragOffset.x}px`, top: `${dragPos.y - dragOffset.y}px` }}
           >
            {draggedItem.type === 'lead' ? (
              <div className="rounded-xl border border-indigo-300 dark:border-indigo-700 bg-white dark:bg-slate-800 shadow-xl p-4 opacity-95">
                <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-2">Lead</div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {draggedItem.CompanyName || draggedItem.ContactName || `Lead #${draggedItem.LeadID}`}
                </div>
                {draggedItem.Title && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{draggedItem.Title}</div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 shadow-xl p-4 opacity-95">
                <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">Opportunity</div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {draggedItem.CustomerName || draggedItem.OpportunityName || `Opportunity #${draggedItem.OpportunityID}`}
                </div>
                {draggedItem.OpportunityName && draggedItem.CustomerName && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{draggedItem.OpportunityName}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

             {showConversionModal && selectedLead && (
         <LeadToOpportunityModal
           lead={selectedLead}
           onSubmit={handleConversionSubmit}
           onCancel={() => {
             setShowConversionModal(false);
             setSelectedLead(null);
           }}
         />
       )}

               {/* Lead Detail Modal */}
        <LeadDetailModal
          lead={selectedLeadForDetail}
          isOpen={showLeadDetailModal}
          onClose={() => {
            setShowLeadDetailModal(false);
            setSelectedLeadForDetail(null);
          }}
          onEdit={(lead) => {
            setShowLeadDetailModal(false);
            setSelectedLeadForDetail(null);
            navigate(`/leads/${lead.LeadID}/edit`);
          }}
        />

        {/* Opportunity Detail Modal */}
        <OpportunityDetailModal
          opportunity={selectedOpportunityForDetail}
          isOpen={showOpportunityDetailModal}
          onClose={() => {
            setShowOpportunityDetailModal(false);
            setSelectedOpportunityForDetail(null);
          }}
          onEdit={(opportunity) => {
            setShowOpportunityDetailModal(false);
            setSelectedOpportunityForDetail(null);
            navigate(`/opportunities/${opportunity.OpportunityID}/edit`);
          }}
        />

     </div>
   );
 };

export default Pipeline;


